import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@acto/web-common/locales";
import type { TodoChecklist as TodoChecklistSchema } from "@acto/genui-components-schema";
import { TodoStatus } from "@acto/gql/graphql";
import type { Todo } from "@acto/taskpane/core/domain";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { useCurrentUser } from "@acto/taskpane/core/data-access/user";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { UndoToast, UndoToastSlot } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { useUndoBatch } from "@acto/taskpane/field-sales-2/utils/hooks/useUndoBatch";
import { styled } from "@pigment-css/react";
import { ROW_EXIT_MS, TodoChecklistCard, TodoChecklistDone, type TodoChecklistRow } from "./TodoChecklistCard";
import { TodoDelegatedCard } from "./TodoDelegatedCard";
import { todoChecklistCardPhase, useMeetingPrepTodosContext } from "./MeetingPrepTodosContext";

interface TodoChecklistProps {
  part: TodoChecklistSchema;
}

const Stack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
}));

function toggled(set: ReadonlySet<string>, id: string, present: boolean): ReadonlySet<string> {
  const next = new Set(set);
  if (present) next.add(id);
  else next.delete(id);
  return next;
}

/**
 * The to-do card: the agents pick the ids, this renders their live state. Completion goes
 * through the shared undo batch (grace window, then `updateTodo`); once the window closes the row
 * folds out of the card, and the undo toast is the only way back. The last row is left in place:
 * the card itself gives way to the all-done confirmation.
 */
export function TodoChecklist({ part }: TodoChecklistProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.todoChecklist");
  const todos = useMeetingPrepTodosContext();
  const { data: viewer } = useCurrentUser();
  const { updateTodos } = useUpdateTodos();
  const track = useTrack();

  const [committed, setCommitted] = useState<ReadonlySet<string>>(new Set());
  /** Completions whose mutation has not answered yet: the confirmation waits for all of them. */
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(new Set());
  const [exitedIds, setExitedIds] = useState<ReadonlySet<string>>(new Set());
  const exitTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const batch = useUndoBatch<Todo>((pending) => {
    for (const [id, todo] of pending) {
      setCommitted((current) => toggled(current, id, true));
      setInFlight((current) => toggled(current, id, true));
      void updateTodos({ input: { id, processedStatus: TodoStatus.Completed } }).then((result) => {
        setInFlight((current) => toggled(current, id, false));
        if (!result) {
          setCommitted((current) => toggled(current, id, false));
          return;
        }
        track(AnalyticsEvents.TODO_COMPLETED, {
          source: "meeting_prep_card",
          todoId: id,
          customerId: todo.client?.id,
          customerName: todo.client?.name,
        });
      });
    }
  });

  const handleComplete = useCallback(
    (id: string) => {
      const todo = todos.todosById.get(id);
      if (!todo) return;
      // A row that came back from a failed completion folds again rather than vanishing at once.
      setExitedIds((current) => (current.has(id) ? toggled(current, id, false) : current));
      batch.schedule(id, todo);
    },
    [todos.todosById, batch],
  );

  const rows = useMemo(
    () =>
      part.todoIds
        .filter((id) => todos.openIdsAtLoad.has(id))
        .map((id) => todos.todosById.get(id))
        .filter((todo): todo is Todo => todo !== undefined)
        .map((todo) => ({
          todo,
          canComplete: !todo.assignee || todo.assignee.id === viewer?.id,
          isCompleting: batch.pending.has(todo.id),
          isCompleted: committed.has(todo.id) || todo.isCompleted(),
        })),
    [part.todoIds, todos.openIdsAtLoad, todos.todosById, viewer?.id, batch.pending, committed],
  );

  const ownRows = useMemo(() => rows.filter((row) => row.canComplete), [rows]);
  const delegatedTodos = useMemo(() => rows.filter((row) => !row.canComplete).map((row) => row.todo), [rows]);
  const hasOpenDelegated = delegatedTodos.some((todo) => !todo.isCompleted());

  const completedOwnIds = useMemo(() => ownRows.filter((row) => row.isCompleted).map((row) => row.todo.id), [ownRows]);
  const allOwnCompleted = ownRows.length > 0 && completedOwnIds.length === ownRows.length;
  // The last row stays put: the card as a whole gives way to the confirmation, and folding the row
  // out first would leave an empty card standing for the grace period.
  const leavingIds = useMemo(() => (allOwnCompleted ? [] : completedOwnIds), [allOwnCompleted, completedOwnIds]);

  useEffect(() => {
    // A failed completion puts its row back: a fold still running for it is cancelled.
    const completed = new Set(completedOwnIds);
    for (const [id, timer] of exitTimers.current) {
      if (completed.has(id)) continue;
      clearTimeout(timer);
      exitTimers.current.delete(id);
    }

    for (const id of leavingIds) {
      if (exitTimers.current.has(id) || exitedIds.has(id)) continue;
      exitTimers.current.set(
        id,
        setTimeout(() => {
          exitTimers.current.delete(id);
          setExitedIds((current) => new Set(current).add(id));
        }, ROW_EXIT_MS),
      );
    }
  }, [completedOwnIds, leavingIds, exitedIds]);

  useEffect(
    () => () => {
      exitTimers.current.forEach(clearTimeout);
    },
    [],
  );

  const leaving = useMemo(() => new Set(leavingIds), [leavingIds]);
  const visibleOwnRows = useMemo<TodoChecklistRow[]>(
    () =>
      ownRows
        .filter((row) => !(row.isCompleted && exitedIds.has(row.todo.id)))
        .map((row) => ({ ...row, isLeaving: leaving.has(row.todo.id) })),
    [ownRows, exitedIds, leaving],
  );

  // All-done: every own row committed and its mutation confirmed — a failure must roll back before
  // the confirmation may show.
  const cardKey = part.cardKey;
  const allOwnCommitted = allOwnCompleted && inFlight.size === 0;
  const report = todos.reportChecklistState;
  useEffect(() => {
    if (!cardKey) return;
    report(cardKey, allOwnCommitted, hasOpenDelegated);
  }, [cardKey, allOwnCommitted, hasOpenDelegated, report]);

  if (rows.length === 0) return null;

  const phase = todoChecklistCardPhase(part, todos);

  return (
    <>
      <Stack>
        {phase === "confirmation" ? (
          <TodoChecklistDone scope="own" />
        ) : phase === "rows" ? (
          <TodoChecklistCard rows={visibleOwnRows} onComplete={handleComplete} />
        ) : null}
        <TodoDelegatedCard todos={delegatedTodos} defaultOpen={ownRows.length === 0} />
      </Stack>
      <UndoToastSlot>
        {batch.pending.size > 0 && (
          <UndoToast
            message={t("completedToast", { count: batch.pending.size })}
            undoLabel={t("undo")}
            onUndo={batch.undo}
          />
        )}
      </UndoToastSlot>
    </>
  );
}
